import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * BondedStoreSearchSelect
 *
 * Searchable dropdown for picking a bonded store from the global list.
 * Results are grouped under their merchant name.
 *
 * When a store is selected, `onSelect` is called with the full store object so
 * the parent can pre-fill name / address / city fields.
 *
 * When the user types something that isn't in the list and `onCustomEntry` is
 * provided, a "Use '[query]' as custom location" option is shown at the bottom
 * of the dropdown.  The parent receives the typed text and is responsible for
 * storing it as free text (e.g. bondedStoreName).
 *
 * Props
 *   value              – currently selected store id (or null)
 *   onSelect           – called with the full store when user picks one
 *   onClear            – called when the user removes the selection
 *   onCustomEntry      – optional; called with typed text for unlisted stores
 *   customLabel        – text to show in the trigger when value is null but a
 *                        custom entry has been made
 *   filterByMerchantId – optionally restrict results to one merchant
 *   placeholder        – input placeholder text
 *   disabled           – disable interaction
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, ChevronDown, X, Check, Plus } from 'lucide-react';
import { merchantsApi } from '../../lib/api';
export function BondedStoreSearchSelect({ value, onSelect, onClear, onCustomEntry, customLabel, filterByMerchantId, placeholder = 'Search bonded stores…', disabled = false, }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    // ── Fetch stores ──────────────────────────────────────────────────────────
    const { data, isLoading } = useQuery({
        queryKey: ['bonded-stores-search', query, filterByMerchantId],
        queryFn: () => merchantsApi.listAllBondedStores({
            q: query.trim() || undefined,
            merchantId: filterByMerchantId ?? undefined,
        }),
        enabled: open,
        staleTime: 30_000,
    });
    const stores = data?.stores ?? [];
    // Fetch the label for the currently selected value on mount
    const { data: selectedStore } = useQuery({
        queryKey: ['bonded-store-name', value],
        queryFn: () => merchantsApi.listAllBondedStores({ q: undefined, merchantId: undefined })
            .then(r => r.stores.find(s => s.id === value) ?? null),
        enabled: Boolean(value),
        staleTime: Infinity,
    });
    // Whether the typed query exactly matches an existing store name (case-insensitive)
    const queryMatchesExisting = query.trim()
        ? stores.some(s => s.name.toLowerCase() === query.trim().toLowerCase())
        : false;
    const showCustomOption = Boolean(onCustomEntry) && query.trim().length > 0 && !queryMatchesExisting;
    // ── Group by merchant ─────────────────────────────────────────────────────
    // Sort by merchant name first so same-merchant stores are always consecutive
    const sortedStores = [...stores].sort((a, b) => a.merchantName.localeCompare(b.merchantName));
    const grouped = [];
    for (const s of sortedStores) {
        const last = grouped[grouped.length - 1];
        if (last && last.merchantName === s.merchantName) {
            last.stores.push(s);
        }
        else {
            grouped.push({ merchantName: s.merchantName, stores: [s] });
        }
    }
    // ── Close on outside click ────────────────────────────────────────────────
    const handleClickOutside = useCallback((e) => {
        if (containerRef.current && !containerRef.current.contains(e.target)) {
            setOpen(false);
            setQuery('');
        }
    }, []);
    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);
    function handleSelect(store) {
        onSelect(store);
        setOpen(false);
        setQuery('');
    }
    function handleCustomEntry() {
        const name = query.trim();
        if (!name || !onCustomEntry)
            return;
        onCustomEntry(name);
        setOpen(false);
        setQuery('');
    }
    // What to show in the closed trigger button
    const displayLabel = value
        ? selectedStore
            ? `${selectedStore.name} (${selectedStore.merchantName})`
            : 'Loading…'
        : customLabel || null;
    const hasSelection = Boolean(value || customLabel);
    // ─── Render ────────────────────────────────────────────────────────────────
    return (_jsxs("div", { ref: containerRef, className: "relative", children: [!open ? (_jsxs("div", { role: "button", tabIndex: disabled ? -1 : 0, "aria-expanded": false, onClick: () => { if (!disabled) {
                    setOpen(true);
                    setTimeout(() => inputRef.current?.focus(), 0);
                } }, onKeyDown: e => { if ((e.key === 'Enter' || e.key === ' ') && !disabled)
                    setOpen(true); }, className: `flex items-center gap-2 w-full rounded-lg border px-3 py-2 text-sm transition-colors ${disabled
                    ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200'
                    : 'bg-white border-gray-300 hover:border-gray-400 cursor-pointer'}`, children: [_jsx(Building2, { className: "h-4 w-4 text-gray-400 shrink-0" }), _jsx("span", { className: `flex-1 truncate ${displayLabel ? 'text-gray-900' : 'text-gray-400'}`, children: displayLabel ?? placeholder }), _jsxs("div", { className: "flex items-center gap-1", children: [hasSelection && !disabled && (_jsx("button", { type: "button", onClick: e => { e.stopPropagation(); onClear(); }, className: "p-0.5 text-gray-400 hover:text-red-500 rounded transition-colors", title: "Clear selection", children: _jsx(X, { className: "h-3.5 w-3.5" }) })), _jsx(ChevronDown, { className: "h-4 w-4 text-gray-400" })] })] })) : (
            /* Search input */
            _jsxs("div", { className: "flex items-center gap-2 w-full rounded-lg border border-wine-400 px-3 py-2 ring-1 ring-wine-200 bg-white", children: [_jsx(Building2, { className: "h-4 w-4 text-gray-400 shrink-0" }), _jsx("input", { ref: inputRef, value: query, onChange: e => setQuery(e.target.value), placeholder: placeholder, className: "flex-1 outline-none text-sm text-gray-900 placeholder:text-gray-400 bg-transparent", onKeyDown: e => {
                            if (e.key === 'Escape') {
                                setOpen(false);
                                setQuery('');
                            }
                            if (e.key === 'Enter' && showCustomOption) {
                                e.preventDefault();
                                handleCustomEntry();
                            }
                        }, autoComplete: "off" }), query && (_jsx("button", { type: "button", onClick: () => setQuery(''), className: "text-gray-400 hover:text-gray-600", children: _jsx(X, { className: "h-3.5 w-3.5" }) }))] })), open && (_jsx("div", { className: "absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-72 overflow-y-auto", children: isLoading ? (_jsx("p", { className: "px-4 py-3 text-sm text-gray-400", children: "Loading\u2026" })) : (_jsxs(_Fragment, { children: [stores.length === 0 && !showCustomOption && (_jsx("p", { className: "px-4 py-3 text-sm text-gray-400", children: "No bonded stores found." })), grouped.map(group => (_jsxs("div", { children: [_jsx("div", { className: "sticky top-0 bg-gray-50 border-b border-gray-100 px-3 py-1.5", children: _jsx("span", { className: "text-[10px] font-semibold text-gray-500 uppercase tracking-wider", children: group.merchantName }) }), group.stores.map(store => (_jsxs("button", { type: "button", onClick: () => handleSelect(store), className: "w-full flex items-start gap-3 px-4 py-2.5 hover:bg-wine-50 text-left transition-colors", children: [_jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx("span", { className: "text-sm font-medium text-gray-800 truncate", children: store.name }), store.isDefaultForMerchant && (_jsx("span", { className: "text-[9px] font-semibold bg-wine-100 text-wine-700 px-1 rounded", children: "Default" }))] }), (store.city || store.address) && (_jsx("p", { className: "text-xs text-gray-400 mt-0.5 truncate", children: [store.city, store.address].filter(Boolean).join(', ') }))] }), store.id === value && _jsx(Check, { className: "h-4 w-4 text-wine-600 shrink-0 mt-0.5" })] }, store.id)))] }, group.merchantName))), showCustomOption && (_jsxs("button", { type: "button", onClick: handleCustomEntry, className: `w-full flex items-center gap-3 px-4 py-2.5 hover:bg-wine-50 text-left transition-colors ${stores.length > 0 ? 'border-t border-gray-100' : ''}`, children: [_jsx(Plus, { className: "h-4 w-4 text-wine-500 shrink-0" }), _jsxs("span", { className: "text-sm text-wine-700", children: ["Use ", _jsxs("span", { className: "font-medium", children: ["\"", query.trim(), "\""] }), " as custom location"] })] }))] })) }))] }));
}
